import { SUBSCRIPTION_LEVELS } from '@sdk/account/constants';
import gettext from '@sdk/lang/gettext';
import { useCurrency } from '@sdk/common/currency/CurrencyContext';
import moneyDisplay from '@sdk/common/currency/moneyDisplay';
import { OrgSettings } from '@sdk/authentication/types/OrganisationType';

export type BillingPlanStatus = {
  name: string;
  description: string;
  price: string;
  isCurrent: boolean;
  hideCallToAction: boolean;
};

type Feature = {
  title: string;
  list: string[];
};

export type BillingPlans = {
  status: Record<number, BillingPlanStatus>;
  features: Feature[];
};

export const useBillingPlans = (
  orgSettings: OrgSettings,
  subscriptionLevel: number,
  isOnTrial: boolean,
  isToastBilling: boolean,
  isPlanAnnual: boolean
): BillingPlans => {
  const { defaultCurrency } = useCurrency();
  const priceMultiplier = isPlanAnnual ? 0.85 : 1;
  return {
    status: {
      [SUBSCRIPTION_LEVELS.FREE]: {
        name: isToastBilling ? gettext('Scheduling Lite') : gettext('Free'),
        description: gettext('Schedule faster, communicate better, and get things done!'),
        price: '$0',
        isCurrent: subscriptionLevel === SUBSCRIPTION_LEVELS.FREE || isOnTrial,
        hideCallToAction: !isOnTrial && subscriptionLevel > SUBSCRIPTION_LEVELS.FREE,
      },
      [SUBSCRIPTION_LEVELS.PREMIUM]: {
        name: gettext('Premium'),
        description: gettext(
          'Track employee hours, and optimize the cost of labor as you schedule!'
        ),
        price: moneyDisplay({
          value: Number(orgSettings.pricePerUser) * priceMultiplier,
          currency: defaultCurrency,
        }),
        isCurrent: subscriptionLevel === SUBSCRIPTION_LEVELS.PREMIUM && !isOnTrial,
        hideCallToAction: !isOnTrial && subscriptionLevel > SUBSCRIPTION_LEVELS.PREMIUM,
      },
      [SUBSCRIPTION_LEVELS.OLD_PREMIUM]: {
        name: gettext('Premium'),
        description: gettext(
          'Track employee hours, and optimize the cost of labor as you schedule!'
        ),
        price: moneyDisplay({
          value: Number(orgSettings.pricePerUser) * priceMultiplier,
          currency: defaultCurrency,
        }),
        isCurrent: subscriptionLevel === SUBSCRIPTION_LEVELS.OLD_PREMIUM && !isOnTrial,
        hideCallToAction: !isOnTrial && subscriptionLevel >= SUBSCRIPTION_LEVELS.OLD_PREMIUM,
      },
      [SUBSCRIPTION_LEVELS.BUSINESS]: {
        name: isToastBilling ? gettext('Scheduling Pro') : gettext('Business'),
        description: gettext(
          'Understand your labor data, and improve the efficiency of your business!'
        ),
        price: moneyDisplay({
          value: Number(orgSettings.businessPricePerUser) * priceMultiplier,
          currency: defaultCurrency,
        }),
        isCurrent: subscriptionLevel === SUBSCRIPTION_LEVELS.BUSINESS && !isOnTrial,
        hideCallToAction: !isOnTrial && subscriptionLevel >= SUBSCRIPTION_LEVELS.BUSINESS,
      },
    },
    features: [
      {
        title: gettext('Scheduling'),
        list: [
          gettext('Shift scheduling'),
          gettext('Shift templates'),
          gettext('Shift alarms'),
          gettext('Shift offers'),
          gettext('Available shifts'),
          gettext('Time off requests'),
          gettext('Long term scheduling'),
          gettext('Shift swaps'),
          gettext('Schedule templates'),
          gettext('Slots on available shifts'),
          gettext('Unavailability'),
          gettext('Day parts'),
          gettext('Google Calendar synchronization'),
          gettext('SMS shift alarms'),
          gettext('Auto-assigning'),
          gettext('Shift acceptance'),
          gettext('Tags'),
        ],
      },
      {
        title: gettext('Communication'),
        list: [
          gettext('News sharing'),
          gettext('Announcements'),
          gettext('Private messaging'),
          gettext('Group messaging'),
        ],
      },
      {
        title: gettext('Time tracking'),
        list: [
          gettext('Mobile time tracking'),
          ...(isToastBilling ? [gettext('Kiosk time tracking')] : []),
          gettext('Timesheets'),
          gettext('Timesheet history'),
          gettext('Timesheet approvals'),
          gettext('Early clock-in prevention'),
          gettext('Auto clock-out'),
          gettext('Geofencing'),
          ...(!isToastBilling ? [gettext('Kiosk time tracking')] : []),
          gettext('Attendance reports'),
          gettext('No shows'),
          gettext('Sick callouts'),
          gettext('Late arrivals'),
        ],
      },
      {
        title: gettext('Labor cost management'),
        list: [
          gettext('Labor cost management'),
          gettext('Budgeting'),
          gettext('Overtime tracking'),
          gettext('Clopening'),
          gettext('Holiday pay'),
          gettext('PTO management'),
          gettext('Payroll reports'),
          gettext('Labor reports'),
          gettext('Sales reports'),
          gettext('Salary support'),
        ],
      },
      {
        title: gettext('Tasks'),
        list: [gettext('Shift tasks'), gettext('Task templates')],
      },
    ],
  };
};
